/** Account, weigh-ins, your foods, recipes, saved meals, AI usage, app. */

import { useEffect, useState } from 'react'

import { api } from '../api/client'
import { db, wipeLocal, type CustomFoodRow, type RecipeRow } from '../db'
import { addWeighin, deleteCustomFood, deleteMeal, deleteWeighin } from '../db/mutations'
import { useCustomFoods, useRecipes, useSavedMeals, useWeighins } from '../db/queries'
import { useApp } from '../context'
import { CustomFoodEditor, draftFromRow } from '../components/CustomFoodEditor'
import { RecipeEditor } from '../components/RecipeEditor'
import { Sheet } from '../components/Sheet'
import { UpdateCheck } from '../components/UpdateCheck'
import { AndroidAppCard } from '../components/GetAndroid'
import { moreEntry } from '../lib/androidPrompt'
import { isNative } from '../native/platform'
import { dayOf } from '../lib/day'
import { shortDate } from '../lib/days'
import { fmtKcal } from '../lib/nutrients'
import { displayToKg, formatWeight } from '../lib/units'
import { subscribe, sync, type SyncState } from '../sync/engine'

type Panel = null | { k: 'food'; row: CustomFoodRow | null } | { k: 'recipe'; row: RecipeRow | null }

export function MoreScreen({ onSignOut }: { onSignOut: () => void }) {
  const { user, unit } = useApp()
  const weighins = useWeighins()
  const foods = useCustomFoods()
  const recipes = useRecipes()
  const meals = useSavedMeals()
  const [panel, setPanel] = useState<Panel>(null)
  const [w, setW] = useState('')
  const [usage, setUsage] = useState<{ spent_usd: number; cap_usd: number } | null>(null)
  const [sync0, setSync0] = useState<SyncState | null>(null)
  useEffect(() => subscribe(setSync0), [])
  useEffect(() => { api<{ spent_usd: number; cap_usd: number }>('/ai/usage').then(setUsage).catch(() => {}) }, [])

  const kg = displayToKg(Number(w), unit)
  return (
    <div>
      <h1>More</h1>
      <div className="card">
        <div className="row"><b>{user.name}</b><span className="muted small">Lift account</span></div>
        <p className="muted small" style={{ marginBottom: 0 }}>Units ({unit}) and time zone come from your Lift account — change them in Lift.</p>
      </div>

      <h2>Weigh-ins</h2>
      <div className="card">
        <div className="row" style={{ gap: '.5rem' }}>
          <input aria-label={`Weight in ${unit}`} inputMode="decimal" placeholder={`Weight (${unit})`} value={w} onChange={(e) => setW(e.target.value)} />
          <button style={{ marginTop: 0 }} disabled={!(kg && kg >= 20 && kg <= 400)} onClick={async () => {
            await addWeighin(kg!, new Date().toISOString()); setW('')
          }}>Add</button>
        </div>
        <p className="muted small">Shared with Lift both ways.</p>
        {[...(weighins ?? [])].reverse().slice(0, 14).map((x) => (
          <div key={x.id} className="food-row">
            <span>{shortDate(dayOf(x.measured_at))}</span>
            <span className="row" style={{ gap: '.8rem' }}>
              <span className="num">{formatWeight(x.weight_kg, unit)}</span>
              {x.relay_state === 'pending' && <span className="tag" title="Will reach Lift on the next sync">not yet in Lift</span>}
              <button className="linky" aria-label="Delete weigh-in" onClick={() => void deleteWeighin(x)}>Delete</button>
            </span>
          </div>
        ))}
      </div>

      <h2>My foods</h2>
      <div className="card" style={{ padding: '.2rem .4rem' }}>
        {(foods ?? []).length === 0 && <p className="muted small" style={{ padding: '.4rem' }}>Foods you create, or read from a label, live here.</p>}
        {(foods ?? []).map((f) => (
          <div key={f.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => setPanel({ k: 'food', row: f })}>
            <div style={{ minWidth: 0 }}><div className="name">{f.name}</div>
              <div className="muted small">{[f.brand, f.barcode ? `barcode ${f.barcode}` : null].filter(Boolean).join(' · ')}</div></div>
            <span className="num muted small">{fmtKcal(f.per100?.kcal ?? 0)} / 100 {f.basis}</span>
          </div>
        ))}
        <button className="linky" style={{ padding: '.6rem .5rem' }} onClick={() => setPanel({ k: 'food', row: null })}>+ New food</button>
      </div>

      <h2>Recipes</h2>
      <div className="card" style={{ padding: '.2rem .4rem' }}>
        {(recipes ?? []).map((r) => (
          <div key={r.id} className="food-row tappable" role="button" tabIndex={0} onClick={() => setPanel({ k: 'recipe', row: r })}>
            <div><div className="name">{r.name}</div><div className="muted small">{r.servings} servings</div></div>
            <span className="num muted small">{fmtKcal(r.per_serving?.kcal ?? 0)} kcal / serving</span>
          </div>
        ))}
        <button className="linky" style={{ padding: '.6rem .5rem' }} onClick={() => setPanel({ k: 'recipe', row: null })}>+ New recipe</button>
      </div>

      <h2>Saved meals</h2>
      <div className="card" style={{ padding: '.2rem .4rem' }}>
        {(meals ?? []).length === 0 && <p className="muted small" style={{ padding: '.4rem' }}>Save a meal from the log with ···.</p>}
        {(meals ?? []).map((m) => (
          <div key={m.id} className="food-row">
            <div><div className="name">{m.name}</div><div className="muted small">{(m.items ?? []).length} items</div></div>
            <button className="linky" onClick={() => void deleteMeal(m)}>Delete</button>
          </div>
        ))}
      </div>

      <h2>AI estimates</h2>
      <div className="card">
        <p style={{ margin: 0 }} className="small">
          {usage ? `$${usage.spent_usd.toFixed(2)} of your $${usage.cap_usd.toFixed(2)} this month.` : 'Usage unavailable offline.'}
        </p>
        <p className="muted small" style={{ marginBottom: 0 }}>Photos are sent once for the estimate and never stored.</p>
      </div>

      {moreEntry(navigator.userAgent, isNative()) !== 'hidden' && (
        <>
          <h2>Android app</h2>
          <AndroidAppCard entry={moreEntry(navigator.userAgent, isNative()) as 'install' | 'link'}
            hasApp={!!user.has_android_app} />
        </>
      )}

      <h2>App</h2>
      <div className="card">
        <UpdateCheck />
        <p className="muted small">
          {sync0?.pending ? `${sync0.pending} change${sync0.pending === 1 ? '' : 's'} waiting to sync. ` : 'Everything is synced. '}
          {sync0?.lastError ? `Last error: ${sync0.lastError}` : ''}
        </p>
        <div className="row" style={{ gap: '.5rem', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          <button className="secondary" onClick={() => void sync()}>Sync now</button>
          <button className="secondary" onClick={async () => {
            if ((await db.outbox.count()) > 0 && !confirm('Some changes haven\'t synced yet and would be lost. Reset anyway?')) return
            await wipeLocal(); void sync()
          }}>Reset local data</button>
          <button className="secondary" onClick={onSignOut}>Sign out</button>
        </div>
      </div>

      {panel?.k === 'food' && (
        <Sheet title={panel.row ? 'Edit food' : 'New food'} onClose={() => setPanel(null)}>
          <CustomFoodEditor initial={panel.row ? draftFromRow(panel.row)
            : { name: '', basis: 'g', serving_label: '', serving_size: null, per100: {} }} onSaved={() => setPanel(null)} />
          {panel.row && <button className="secondary" style={{ width: '100%', color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={async () => { await deleteCustomFood(panel.row!); setPanel(null) }}>Delete food</button>}
        </Sheet>
      )}
      {panel?.k === 'recipe' && (
        <Sheet title={panel.row ? 'Edit recipe' : 'New recipe'} onClose={() => setPanel(null)}>
          <RecipeEditor recipe={panel.row} onDone={() => setPanel(null)} />
        </Sheet>
      )}
    </div>
  )
}
